import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Blog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Scroll to top when the blog page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  
  // Function to navigate to homepage and scroll to a section
  const navigateToHomeSection = (sectionId) => {
    navigate(`/${sectionId}`);
  };

  return (
    <div className="bg-gray-50">
      {/* Blog Hero Section */}
      <section className="bg-gradient-to-br from-primary-700 to-primary-800 text-white py-16">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Golf Tournament Software: Choosing the Right Solution for Your Course</h1>
            <p className="text-xl opacity-90">Published: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
      </section>
      
      {/* Blog Content */}
      <section className="py-12">
        <div className="container">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
            <article className="p-8 md:p-12">
              <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-primary-800 prose-p:text-gray-700 prose-p:leading-relaxed prose-li:text-gray-700 prose-li:my-1">
                <p className="text-xl text-gray-800 mb-8 leading-relaxed">
                  Managing golf tournaments efficiently can be the difference between a memorable event that players rave about and a logistical nightmare. With several options on the market today, how do you know which tournament management software is right for your course? Let's compare Golf Marshal with some of the leading competitors: Golf Genius, Live Tourney, and Event Caddy.
                </p>
                
                <h2 className="mt-10 mb-5">The Challenge of Golf Tournament Management</h2>
                <p className="mb-4">
                  Golf professionals face numerous challenges when running tournaments and leagues:
                </p>
                <ul className="space-y-1 mb-6">
                  <li>Time-consuming manual scoring and calculations</li>
                  <li>Managing player registrations across multiple events</li>
                  <li>Communicating effectively with participants</li>
                  <li>Creating fair flights and pairings</li>
                  <li>Offering real-time leaderboards and results</li>
                </ul>
                <p className="mb-10">
                  The right software solution should address these challenges while being easy to use and affordable. Let's see how Golf Marshal compares to other options.
                </p>
                
                <h2 className="mt-12 mb-5">Golf Marshal vs Golf Genius</h2>
                <p className="mb-6">
                  <strong>Golf Genius</strong> has been in the market for years and offers a comprehensive set of features for tournament management. However, there are some key differences worth noting:
                </p>
                
                <div className="bg-gray-50 p-6 rounded-lg my-6 shadow-sm">
                  <h3 className="text-xl font-bold mb-4 text-primary-700">Key Differentiators</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold text-primary-600 mb-3">Golf Genius</h4>
                      <ul className="mt-2 space-y-3">
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Expensive with setup and onboarding fees</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Complex interface with steep learning curve</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Requires app downloads for mobile scoring</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Extensive feature set for large operations</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-primary-600 mb-3">Golf Marshal</h4>
                      <ul className="mt-2 space-y-3">
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Affordable pricing with no hidden fees</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Intuitive interface with minimal learning curve</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>No app downloads required for mobile scoring</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Dedicated, responsive support team based in Missouri</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <p className="mb-10">
                  While Golf Genius offers a comprehensive system, Golf Marshal delivers comparable core functionality with a more approachable interface and pricing structure. The elimination of app downloads for players is particularly significant, as it removes a common friction point in tournament participation.
                </p>
                
                <h2 className="mt-12 mb-5">Golf Marshal vs Live Tourney</h2>
                <p className="mb-6">
                  <strong>Live Tourney</strong> positions itself as a simpler alternative to more complex systems. Here's how it compares to Golf Marshal:
                </p>
                
                <div className="bg-gray-50 p-6 rounded-lg my-6 shadow-sm">
                  <h3 className="text-xl font-bold mb-4 text-primary-700">Key Comparisons</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold text-primary-600 mb-3">Live Tourney</h4>
                      <ul className="mt-2 space-y-3">
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Simple interface focused on tournaments</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Web-based mobile scoring</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Free trial available</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Limited league management capabilities</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-primary-600 mb-3">Golf Marshal</h4>
                      <ul className="mt-2 space-y-3">
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Intuitive interface for both tournaments and leagues</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Web-based mobile scoring with offline capabilities</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Free trial with full access to all features</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Comprehensive league management with season standings</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <p className="mb-10">
                  Both Live Tourney and Golf Marshal aim to simplify tournament management, but Golf Marshal offers more robust league management capabilities and offline scoring functionality, which is crucial for courses with limited connectivity on certain holes.
                </p>

                <h2 className="mt-12 mb-5">Golf Marshal vs Event Caddy</h2>
                <p className="mb-6">
                  <strong>Event Caddy</strong> focuses on event management with an emphasis on marketing and promotion. Here's how it stacks up against Golf Marshal:
                </p>
                
                <div className="bg-gray-50 p-6 rounded-lg my-6 shadow-sm">
                  <h3 className="text-xl font-bold mb-4 text-primary-700">Feature Comparison</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold text-primary-600 mb-3">Event Caddy</h4>
                      <ul className="mt-2 space-y-3">
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Strong marketing and promotion tools</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Event website creation</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Less focus on day-of tournament operations</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Limited league management capabilities</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-primary-600 mb-3">Golf Marshal</h4>
                      <ul className="mt-2 space-y-3">
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Integrated email and text communication tools</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Comprehensive day-of tournament operations</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Full league management functionality</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Real-time leaderboard updates</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <p className="mb-10">
                  Event Caddy excels in marketing and promotion, while Golf Marshal provides a more comprehensive solution for the actual operation of tournaments and leagues. For courses seeking an all-in-one solution, Golf Marshal offers better day-of-event management capabilities.
                </p>
                
                <h2 className="mt-12 mb-5">Key Factors to Consider When Choosing Tournament Software</h2>
                
                <h3 className="mt-8 mb-3 text-xl text-primary-700">1. User Experience</h3>
                <p className="mb-6">
                  The easier your software is to use, the more likely your staff and players will adopt it. Golf Marshal was designed with simplicity in mind, eliminating unnecessary complexity while still providing powerful features.
                </p>
                
                <h3 className="mt-8 mb-3 text-xl text-primary-700">2. Mobile Scoring Experience</h3>
                <p className="mb-6">
                  Players increasingly expect modern mobile scoring options. Golf Marshal's web-based approach means no app downloads are required—players simply access a URL on their phones to enter scores, view leaderboards, and engage with the tournament.
                </p>
                
                <h3 className="mt-8 mb-3 text-xl text-primary-700">3. Support and Training</h3>
                <p className="mb-6">
                  Even the best software requires occasional support. Golf Marshal's dedicated Missouri-based support team provides responsive assistance when you need it, rather than outsourced support with limited knowledge of golf operations.
                </p>
                
                <h3 className="mt-8 mb-3 text-xl text-primary-700">4. Pricing Structure</h3>
                <p className="mb-6">
                  Look beyond the monthly fee to understand the total cost of ownership. Golf Marshal's transparent pricing includes all features without hidden setup fees or charges for additional users.
                </p>
                
                <h3 className="mt-8 mb-3 text-xl text-primary-700">5. Offline Capabilities</h3>
                <p className="mb-6">
                  Many golf courses have areas with limited connectivity. Golf Marshal's mobile scoring works offline and automatically syncs when connectivity is restored, ensuring uninterrupted scoring regardless of network coverage.
                </p>
                
                <h2 className="mt-12 mb-6">Real Results from Golf Marshal Customers</h2>
                <blockquote className="border-l-4 border-primary-500 pl-4 italic my-8 bg-gray-50 p-6 rounded-lg">
                  "We started using Golf Marshal in the Spring of 2022. We can run our tournaments more quickly, communicate weekly league standings, produce custom scorecards, and so much more. Plus, customer support is a simple phone call away with the product made in our home state of Missouri."
                  <footer className="text-sm mt-3 not-italic font-medium">— Josh Huelsing, General Manager, Crystal Highlands Golf Course</footer>
                </blockquote>
                
                <blockquote className="border-l-4 border-primary-500 pl-4 italic my-8 bg-gray-50 p-6 rounded-lg">
                  "Golf Marshal has been a game changer for our golf course when it comes to tournaments and outings. We no longer have to manually do flights, skins, and scores. With a click of a button everything is sorted out. The amount of time and effort that Golf Marshal saves makes it a no-brainer for courses looking to upgrade their tournament experience."
                  <footer className="text-sm mt-3 not-italic font-medium">— Chad Schilli, General Manager, Ste. Genevieve Golf Club</footer>
                </blockquote>
                
                <h2 className="mt-12 mb-5">Conclusion: Finding the Right Fit</h2>
                <p className="mb-4">
                  While all these platforms offer valuable features, Golf Marshal stands out for its combination of powerful functionality, ease of use, and value. The focus on an intuitive interface means less time training staff and more time running successful events.
                </p>
                
                <p className="mb-8">
                  For courses seeking a comprehensive solution that players will actually use, Golf Marshal delivers the right balance of powerful features and simplicity. The elimination of app downloads and the inclusion of offline scoring capabilities address two common pain points in golf tournament management.
                </p>
                
                <div className="bg-primary-50 border border-primary-100 rounded-lg p-8 my-10 shadow-md">
                  <h3 className="text-2xl font-bold text-primary-700 mb-4">Ready to elevate your golf events?</h3>
                  <p className="mb-6 text-lg">
                    Experience the difference Golf Marshal can make for your course with our free 30-day trial. No credit card required, and full access to all features.
                  </p>
                  <a 
                    href="https://www.getgolfmarshal.com/signuppro" 
                    className="btn btn-primary inline-block text-lg px-8 py-3">
                    Start Your Free Trial
                  </a>
                </div>
                
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog; 